import { Controller } from "@hotwired/stimulus"

const CREATE_PATIENT_MODAL_ID = "create-profile-patient"
const EMPTY_DETAIL = "—"

// "2026-10-06" → « Mardi 06 octobre 2026 », read as a calendar day (no time zone shift).
export function formatSessionDate (isoDate) {
  const [year, month, day] = String(isoDate).split("-").map(Number)
  if (!year || !month || !day) return isoDate

  const label = new Date(year, month - 1, day).toLocaleDateString("fr-FR", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric"
  })
  return label.charAt(0).toUpperCase() + label.slice(1)
}

export default class extends Controller {
  static targets = [
    "companySelect",
    "siteSelect",
    "sessionSelect",
    "slotSelect",
    "practitionerId",
    "siteDetails",
    "sessionDetails"
  ]

  static values = {
    sitesUrl: String,
    sessionsUrl: String,
    practitionerUrl: String
  }

  connect () {
    this.createPatientModal = document.getElementById(CREATE_PATIENT_MODAL_ID)
    this.onCreatePatientModalShow = () => this.syncPatientSiteClassifications()
    this.createPatientModal?.addEventListener("show.bs.modal", this.onCreatePatientModalShow)

    if (this.hasCompanySelectTarget && this.companySelectTarget.value) {
      this.loadSites()
    }
  }

  disconnect () {
    this.createPatientModal?.removeEventListener("show.bs.modal", this.onCreatePatientModalShow)
  }

  companyChanged () {
    this.resetSiteSelect()
    this.resetSessionSelect()
    this.resetSlotSelect()
    this.hideSiteDetails()
    this.hideSessionDetails()
    this.hideBookingFields()
    this.syncPatientSiteClassifications()

    if (!this.companySelectTarget.value) return

    this.loadSites()
  }

  siteChanged () {
    this.resetSessionSelect()
    this.resetSlotSelect()
    this.hideSessionDetails()
    this.hideBookingFields()
    this.syncPatientSiteClassifications()

    if (!this.siteSelectTarget.value) {
      this.hideSiteDetails()
      return
    }

    this.showSiteDetails()
    this.loadSessions()
  }

  sessionChanged () {
    this.resetSlotSelect()
    this.hideBookingFields()

    if (!this.sessionSelectTarget.value) {
      this.hideSessionDetails()
      if (this.hasPractitionerIdTarget) this.practitionerIdTarget.value = ""
      return
    }

    const selectedOption = this.sessionSelectTarget.selectedOptions[0]
    if (this.hasPractitionerIdTarget) {
      this.practitionerIdTarget.value = selectedOption.dataset.practitionerId || ""
    }

    this.showSessionDetails()
    this.populateSlots()
    this.showBookingFields()
  }

  async loadSites () {
    const response = await fetch(`${this.sitesUrlValue}?by_company_id=${this.companySelectTarget.value}`, {
      headers: { Accept: "application/json" }
    })
    const data = await response.json()
    const options = data.data.map(site => ({
      value: String(site.id),
      text: site.attributes.name,
      address: site.attributes.address?.display_name || "",
      appointmentRoom: site.attributes.appointment_room || "",
      contributorsInstructions: site.attributes.contributors_instructions || "",
      amazon: Boolean(site.attributes.amazon),
      aldes: Boolean(site.attributes.aldes)
    }))

    this.setSiteOptions(options)

    if (options.length === 1) {
      this.setSelectValue(this.siteSelectTarget, options[0].value)
    } else {
      this.syncPatientSiteClassifications()
    }
  }

  async loadSessions () {
    const response = await fetch(`${this.sessionsUrlValue}?by_site_id=${this.siteSelectTarget.value}`, {
      headers: { Accept: "application/json" }
    })
    const data = await response.json()

    this.sessionSelectTarget.innerHTML = `<option value="">${this.sessionSelectTarget.dataset.placeholder}</option>`

    data.data
      .filter(session => (session.attributes.available_slots || []).length > 0)
      .forEach(session => {
        const option = document.createElement("option")
        option.value = session.id
        option.textContent = formatSessionDate(session.attributes.date)
        option.dataset.practitionerId = session.attributes.practitioner_id
        option.dataset.appointmentDuration = session.attributes.appointment_duration
        option.dataset.availableSlots = JSON.stringify(session.attributes.available_slots || [])
        option.dataset.practitionerName = session.attributes.practitioner_full_name || ""
        this.sessionSelectTarget.appendChild(option)
      })
  }

  populateSlots () {
    const selectedOption = this.sessionSelectTarget.selectedOptions[0]
    const slots = JSON.parse(selectedOption.dataset.availableSlots || "[]")

    this.slotSelectTarget.innerHTML = `<option value="">${this.slotSelectTarget.dataset.placeholder}</option>`
    slots.forEach(slot => {
      const option = document.createElement("option")
      option.value = slot
      option.textContent = slot
      this.slotSelectTarget.appendChild(option)
    })
  }

  showSiteDetails () {
    const details = this.selectedSiteDetails()
    const cell = (name) => this.siteDetailsTarget.querySelector(`[data-${name}]`)

    this.fillDetail(cell("site-address"), details.address, { href: this.mapsUrl(details.address) })
    this.fillDetail(cell("site-room"), details.appointmentRoom)
    this.fillDetail(cell("site-instructions"), details.contributorsInstructions)
  }

  hideSiteDetails () {
    this.resetDetails(this.siteDetailsTarget)
  }

  showSessionDetails () {
    const selectedOption = this.sessionSelectTarget.selectedOptions[0]
    const durationCell = this.sessionDetailsTarget.querySelector("[data-session-duration]")
    const duration = selectedOption.dataset.appointmentDuration

    this.loadPractitioner(selectedOption.value)
    this.fillDetail(durationCell, duration ? `${duration} ${durationCell.dataset.suffix}` : "")
  }

  // The practitioner's profile cell is server-rendered: its turbo-frame loads it for the session.
  loadPractitioner (sessionId) {
    const frame = this.practitionerFrame()
    if (!frame || !this.hasPractitionerUrlValue) return

    frame.setAttribute("src", this.practitionerUrlValue.replace("SESSION_ID", encodeURIComponent(sessionId)))
  }

  practitionerFrame () {
    return this.sessionDetailsTarget.querySelector("[data-session-practitioner] turbo-frame")
  }

  hideSessionDetails () {
    this.resetDetails(this.sessionDetailsTarget)
  }

  // The details tables stay in place (no layout shift): a value, a dash when the record has none, or the placeholder.
  fillDetail (cell, value, { href } = {}) {
    if (!cell) return

    cell.replaceChildren()
    if (!value) {
      cell.textContent = EMPTY_DETAIL
      return
    }

    if (!href) {
      cell.textContent = value
      return
    }

    const link = document.createElement("a")
    link.href = href
    link.target = "_blank"
    link.rel = "noopener"
    link.textContent = value
    cell.appendChild(link)
  }

  resetDetails (table) {
    table.querySelectorAll("td").forEach(cell => {
      const placeholder = document.createElement("span")
      placeholder.className = "text-muted"
      placeholder.textContent = table.dataset.placeholder
      // A cell holding a turbo-frame (the practitioner) keeps it, emptied, for the next session.
      const frame = cell.querySelector("turbo-frame")
      if (frame) {
        frame.removeAttribute("src")
        frame.replaceChildren(placeholder)
        return
      }
      cell.replaceChildren(placeholder)
    })
  }

  mapsUrl (address) {
    if (!address) return null

    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`
  }

  // The slot list stays in its row, disabled until a session gives it slots.
  showBookingFields () {
    this.slotSelectTarget.disabled = false
  }

  hideBookingFields () {
    this.slotSelectTarget.disabled = true
  }

  resetSiteSelect () {
    this.setSiteOptions([])
  }

  resetSessionSelect () {
    this.sessionSelectTarget.innerHTML = `<option value="">${this.sessionSelectTarget.dataset.placeholder}</option>`
    this.sessionSelectTarget.value = ""
    if (this.hasPractitionerIdTarget) this.practitionerIdTarget.value = ""
  }

  resetSlotSelect () {
    this.slotSelectTarget.innerHTML = `<option value="">${this.slotSelectTarget.dataset.placeholder}</option>`
    this.slotSelectTarget.value = ""
  }

  setSiteOptions (options) {
    const tomSelect = this.siteSelectTarget.tomselect

    if (tomSelect) {
      tomSelect.clear(true)
      tomSelect.clearOptions()
      tomSelect.addOptions(options)
      return
    }

    this.siteSelectTarget.innerHTML = "<option value=\"\"></option>"
    options.forEach(siteOption => {
      const option = document.createElement("option")
      option.value = siteOption.value
      option.textContent = siteOption.text
      option.dataset.address = siteOption.address
      option.dataset.appointmentRoom = siteOption.appointmentRoom
      option.dataset.contributorsInstructions = siteOption.contributorsInstructions
      option.dataset.amazon = siteOption.amazon ? "true" : "false"
      option.dataset.aldes = siteOption.aldes ? "true" : "false"
      this.siteSelectTarget.appendChild(option)
    })
  }

  setSelectValue (select, value) {
    const tomSelect = select.tomselect
    if (tomSelect) {
      tomSelect.setValue(String(value))
      return
    }

    select.value = value
    select.dispatchEvent(new Event("change", { bubbles: true }))
  }

  selectedSiteDetails () {
    const tomSelect = this.siteSelectTarget.tomselect
    const selectedValue = this.siteSelectTarget.value

    if (tomSelect) {
      const option = tomSelect.options[selectedValue] || {}
      return {
        address: option.address || "",
        appointmentRoom: option.appointmentRoom || "",
        contributorsInstructions: option.contributorsInstructions || "",
        amazon: Boolean(option.amazon),
        aldes: Boolean(option.aldes)
      }
    }

    const selectedOption = this.siteSelectTarget.selectedOptions[0]
    return {
      address: selectedOption?.dataset?.address || "",
      appointmentRoom: selectedOption?.dataset?.appointmentRoom || "",
      contributorsInstructions: selectedOption?.dataset?.contributorsInstructions || "",
      amazon: selectedOption?.dataset?.amazon === "true",
      aldes: selectedOption?.dataset?.aldes === "true"
    }
  }

  syncPatientSiteClassifications () {
    const modal = this.createPatientModal || document.getElementById(CREATE_PATIENT_MODAL_ID)
    if (!modal) return

    const details = this.selectedSiteDetails()
    const siteIdInput = document.getElementById(`${CREATE_PATIENT_MODAL_ID}-site-id`)
    if (siteIdInput) siteIdInput.value = this.siteSelectTarget.value || ""

    this.toggleClassificationField(
      modal.querySelector('[data-patient-site-classification="employee"]'),
      details.aldes
    )
    this.toggleClassificationField(
      modal.querySelector('[data-patient-site-classification="amazon"]'),
      details.amazon
    )
  }

  toggleClassificationField (field, visible) {
    if (!field) return

    field.classList.toggle("d-none", !visible)
    if (!visible) this.clearClassificationInputs(field)
  }

  clearClassificationInputs (field) {
    field.querySelectorAll("select, input").forEach(input => {
      if (input.type === "radio" || input.type === "checkbox") {
        input.checked = false
        return
      }

      const tomSelect = input.tomselect
      if (tomSelect) {
        tomSelect.clear(true)
        return
      }

      input.value = ""
    })
  }
}
